import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { API, LMSBASEURL } from "../config/apiUrls";
import { formatDate } from "../utils/utils";
import { Link } from "react-router-dom";
import { File } from "lucide-react"
import { Modal, OverlayTrigger } from "react-bootstrap";
import { Tooltip } from "react-bootstrap";
import { isMobile } from "react-device-detect";

export const CustomModal = ({ show, onClose, title, children, width = 920, hideCloseButton = false, closeOnBackdropClick = false, hideCloseIon = false }) => {
  const modalRef = useRef();
  const backdropRef = useRef();

  useEffect(() => {
    if (show && modalRef.current) {
      requestAnimationFrame(() => {
        modalRef.current.classList.add("custom-modal-content--open");
      });
    }
    return () => {
      if (modalRef.current) {
        modalRef.current.classList.remove("custom-modal-content--open");
      }
    };
  }, [show]);

  const handleBackdropClick = (e) => {
    if (
      closeOnBackdropClick &&
      backdropRef.current &&
      e.target === backdropRef.current
    ) {
      onClose?.();
    }
  };

  if (!show) return null;
  return (
    <div className="custom-modal-backdrop" ref={backdropRef} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className="custom-modal-content"
        style={{
          maxWidth: width,
          background: hideCloseButton ? "none" : "#fff",
          padding: hideCloseButton ? "0px" : "40px"
        }}
      >
        <div className={hideCloseButton ? "d-flex justify-content-between align-items-center" : "d-flex justify-content-between align-items-center mb-3"}>
          {title && <h4 className="custom-modal-title m-0">{title}</h4>}
        </div>
        {!hideCloseIon && (
          <button onClick={onClose} className="custom-modal-close">
            &times;
          </button>
        )}
        {/* <button onClick={onClose} className="custom-modal-close">&times;</button> */}

        {children}
      </div>
      <style>{`
        .custom-modal-backdrop {
          position: fixed;
          top: 0; left: 0; width: 100vw; height: 100vh;
          background: rgba(0,0,0,0.5);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px; /* Prevent modal touching edges */
          overflow: auto; /* Ensure scroll inside modal box */
        }

        @keyframes fadeInBackdrop {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .custom-modal-content {
          width: 100%;
          max-height: 100%;
          overflow-y: auto;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2); 
          position: relative;
          opacity: 0;
          transform: scale(0.9) translateY(20px);
          transition: opacity 0.3s ease, transform 0.3s ease;
        }

        .custom-modal-content--open {
          opacity: 1;
          transform: scale(1) translateY(0);
        }

        .custom-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          font-size: 1.5rem;
          color: #666;
          cursor: pointer;
          transition: color 0.2s;
          z-index: 1;
        }

        .custom-modal-close:hover {
          color: #333;
        }

        .custom-modal-title {
          margin-bottom: 24px;
          font-weight: bold;
          font-size: 1.5rem;
          color: #333;
        }
      `}</style>
    </div>
  );
};

const ResumeContent = React.forwardRef((props, ref) => {
  const { studentDetails, studentAccomplishment, studentEducation, studentExperience, studentStats } = props;
  return (
    <div ref={ref}
      className="resume-a4-page p-4 position-relative"
      style={{
        width: "794px",
        height: "1123px",
        backgroundColor: "#fff",
        overflowY: "auto",
        position: "relative",
        margin: "0 auto"
      }}
    >
      <div className="text-center mb-4">
        <h2 className="text-uppercase fw-bold" >
          {studentDetails?.first_name} {studentDetails?.last_name}
        </h2>
        <span className="mb-1 text-dark">
          {studentDetails?.student_detail?.address1 != null ? <>
            {`${studentDetails?.student_detail?.address1} ${studentDetails?.student_detail?.address2}`
              || 'Address not provided'}
          </> : <>Address not provided</>}

        </span><br />
        <span className="mb-1 text-dark">
          <i class="ri-phone-line me-1"></i>{studentDetails?.contact_number} ,
          {" "}<i class="ri-mail-line me-1"></i>{studentDetails?.email} ,
          {" "}<i class="ri-map-pin-line me-1"></i>
          {studentDetails?.student_detail?.city?.name != null ? studentDetails?.student_detail?.city?.name + ',' : 'No City'}
          {studentDetails?.student_detail?.state?.name != null && studentDetails?.student_detail?.state?.name + ','}
        </span>
      </div>


      <section className="mb-2 text-dark">
        <hr />
        <h4>Summary</h4>
        {studentDetails["student_detail"]?.profile_summary != null ?
          <p>{studentDetails["student_detail"] ? studentDetails["student_detail"]?.profile_summary : "No data found"}</p>
          : <>No Profile Summary Found.</>}
      </section>


      <section className="mb-2 text-dark">
        <hr />
        <h4>Work Experience</h4>
        {studentExperience && studentExperience?.length > 0 ? <>
          {studentExperience?.map((exp) => (
            <div className="mb-3" key={exp?.id}>
              <div className="d-flex justify-content-between">
                <h5 className="fw-bold mb-0 text-dark">{exp?.designation}, {" "} {exp?.company}</h5>
                <p className="text-dark fw-bold mb-0">
                  {formatDate(exp.start_date)} - {" "}
                  {exp?.currently_working === "yes" ? "Present" : formatDate(exp?.end_date)}
                </p>
              </div>
              {exp?.job_summary && (
                <p className="mt-2 mb-0" style={{ marginLeft: 30 }}>
                  {exp.job_summary}
                </p>
              )}
            </div>
          ))}
        </> : <>No Work Experience Found.</>}
      </section>
      <hr />


      <section className="mb-2 text-dark">
        <h4>Education</h4>
        <div>
          {studentEducation && studentEducation?.length > 0 ? <>
            {studentEducation?.map((edu) => {
              return (
                <Fragment key={edu?.id}>
                  <div className="d-flex justify-content-between">
                    <h5 className="mb-0 text-dark"><strong>{edu?.degree_title}</strong></h5>
                    <p className="mb-0 text-dark fw-bold ">{`${edu?.is_studying === 1 ? "(Currently Studying)" : (`${edu?.year}`)}`}</p>
                  </div>
                  <p className="text-dark">{edu?.institute_name} {`${edu?.is_studying === 1 ? "" : (`, ${edu?.year}`)}`}</p>
                </Fragment>
              )
            })}
          </> : <>No Education Found</>}
        </div>
        <hr />
      </section>
      <section className="mb-2 text-dark">
        <h4>Skills</h4>
        <div>
          {studentDetails["student_skill"] &&
            <p className="text-dark">
              {studentDetails["student_skill"]?.length > 0 ? studentDetails["student_skill"]?.map(skill => skill?.skill_title).join(', ') : 'No Skills Found.'}
            </p>}
        </div>
        <hr />
      </section>

      <section className="text-dark">
        <h4>Additional Information</h4>
        <ul>
          {/* {studentDetails["student_skill"] && <li><strong>Technical Skills: </strong>{studentDetails["student_skill"]?.length>0 ? studentDetails["student_skill"]?.map(skill => skill?.skill_title).join(', '):'No Skills Found.'}</li>} */}
          {studentDetails["student_language"] && <li><strong>Languages: </strong>{studentDetails["student_language"]?.length > 0 ? studentDetails["student_language"]?.map(lan => lan?.language).join(', ') : 'No Languages Found.'}</li>}
          {studentAccomplishment && (
            <li>
              <strong>Certifications: </strong>
              {studentAccomplishment?.length > 0 ? <>
                {studentAccomplishment?.map((cert, index) => (
                  <span key={cert?.id}>
                    <Link className="text-capitalize text-dark" to={cert?.certificate_url} target="_blank" rel="noopener noreferrer">
                      {cert?.title}
                    </Link>
                    {index !== studentAccomplishment.length - 1 && ', '}
                  </span>
                ))}
              </> : <>
                No Certifications Found.
              </>}
            </li>
          )}
        </ul>
      </section>
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          right: "10px",
          width: 100,
          height: "auto",
          zIndex: 0,
          // filter: "brightness(1.5) contrast(1.2)"
        }}
      >
        <img
          src="/assets/images/certificate/certificate_logo.png"
          alt="Watermark"
          style={{ width: "100%", height: "auto" }}
        />
      </div>

    </div>
  )
});

const Resume = ({ id, isStidentList = false, isManualOpen = false }) => {
  const componentRef = useRef();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false)
  const [studentDetails, setStudentDetails] = useState({})
  const [studentAccomplishment, setStudentAccomplishment] = useState([]);
  const [studentEducation, setStudentEducation] = useState([]);
  const [studentExperience, setStudentExperience] = useState([]);
  const [studentStats, setStudentStats] = useState({});

  const token = sessionStorage.getItem("token");
  const role = sessionStorage.getItem("role");
  const APItoken = process.env.REACT_APP_API_TOKEN;
  const AWS = process.env.REACT_APP_AWS_HOST;

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const lmsHeaders = {
    Authorization: APItoken,
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const handleSampleResume = () => {
    window.open(`${AWS}uploads/Sample_Resume.pdf`, "_blank");
  };


  const fetchStudentDetails = useCallback(async () => {
    setLoading(true)
    try {
      const queries = [
        fetch(`${API}/api/student/get-student?id=${id}`, { headers }),
        fetch(`${LMSBASEURL}/api/accomplishments?studentId=${id}`, { headers: lmsHeaders }),
        fetch(`${LMSBASEURL}/api/education?studentId=${id}`, { headers: lmsHeaders }),
        fetch(`${LMSBASEURL}/api/experinces?student_id=${id}`, { headers: lmsHeaders }),
        fetch(`${LMSBASEURL}/api/student/dashboard?student_id=${id}`, { headers: lmsHeaders }),
      ];

      const [
        studentRes,
        accomplishmentRes,
        educationRes,
        experienceRes,
        statsRes,
      ] = await Promise.all(queries);

      const [
        studentData,
        accomplishmentData,
        educationData,
        experienceData,
        statsData,
      ] = await Promise.all([
        studentRes.json(),
        accomplishmentRes.json(),
        educationRes.json(),
        experienceRes.json(),
        statsRes.json(),
      ]);

      if (!studentRes.ok) throw new Error(studentData.message || "Failed to load student");
      if (!accomplishmentRes.ok) throw new Error(accomplishmentData.message || "Failed to load accomplishments");
      if (!educationRes.ok) throw new Error(educationData.message || "Failed to load education");
      if (!experienceRes.ok) throw new Error(experienceData.message || "Failed to load experience");
      if (!statsRes.ok) throw new Error(statsData.message || "Failed to load stats");

      setStudentDetails(studentData);
      setStudentAccomplishment(accomplishmentData?.data || []);
      setStudentEducation(educationData?.data || []);
      setStudentExperience(experienceData?.data || []);
      setStudentStats(statsData);
      setShowModal(true)
    } catch (err) {
      setShowModal(false)
      console.error("Error fetching student data:", err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const downloadPDF = async () => {
    const element = componentRef.current;
    if (!element) return;

    const canvas = await html2canvas(element, {
      scale: 4,
      useCORS: true,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/jpeg', 1);
    const pdf = new jsPDF('p', 'mm', 'a4');

    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = 210;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${studentDetails?.first_name}_${studentDetails?.last_name}_Resume.pdf`);
  };

  return (
    <>
      {isStidentList ?
        isManualOpen ?
          <OverlayTrigger
            delay={{ hide: 450, show: 300 }}
            overlay={(props) => (
              <Tooltip {...props}>
                View and Download Resumes
              </Tooltip>
            )}
            placement="auto"
          >
            {loading ? <i className="ri-loader-4-line spin text-black text-lg"></i> : <File size={18} color="black" onClick={fetchStudentDetails} className="cursor-pointer" />}
          </OverlayTrigger>
          :
          <button
            className={`btn btn-sm flex-fill w-100 btn-success`}
            onClick={fetchStudentDetails}
            style={{ fontSize: "12px", fontWeight: "500", padding: "6px 12px" }}
          >
            {loading ? "Loading..." : "View Resume"}
          </button>
        :
        <button onClick={fetchStudentDetails} className="btn btn-primary w-100 btn-sm rounded-pill fw-semibold">
          {loading ? "Loading..." : <><i className="bi bi-download me-2"></i>Resume</>}
        </button>}

      <Modal size='xl' show={showModal} onHide={() => setShowModal(false)} animation={false}>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex gap-3 w-100 justify-content-between align-items-center">
            Resume Preview
            {isMobile ? <button
              onClick={downloadPDF}
              className={`btn  btn-primary btn-sm ml-3`}
            >
              <i className="bi bi-download me-1"></i>
            </button> :
              <div className="d-flex justify-content-center gap-2 mb-3">
                {role === "Student" && (
                  <button
                    onClick={handleSampleResume}
                    className={`btn btn-outline-primary btn-sm rounded-pill fw-semibold mt-3`}
                  >
                    <i className="bi bi-file-earmark-text me-1"></i> Sample Resume
                  </button>
                )}
                <button
                  onClick={downloadPDF}
                  className={`btn  btn-primary btn-sm rounded-pill fw-semibold mt-3`}
                >
                  <i className="bi bi-download me-1"></i> Download
                </button>
              </div>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: isMobile ? "70vh" : "85vh", overflowY: "auto" }}>
          {/* {!isMobile &&<div className="d-flex justify-content-center mb-3">
            <button
              onClick={downloadPDF}
              className={`btn  btn-primary btn-sm rounded-pill fw-semibold ${isMobile ? 'w-50':'w-25'}`}
            >
              <i className="bi bi-download me-1"></i> Download
            </button>
          </div>} */}
          <div className="border rounded">
            <ResumeContent
              studentAccomplishment={studentAccomplishment}
              studentEducation={studentEducation}
              studentExperience={studentExperience}
              studentStats={studentStats}
              studentDetails={studentDetails}
              ref={componentRef} />
          </div>

        </Modal.Body>
      </Modal>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .spin {
            display: inline-block;
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </>
  );
};

export default Resume;